import { useMutation, gql } from '@apollo/client';

import { useShowMessage } from '../../core-ui';
import { confirm } from '../App/AppDialog';

import type {
  ClearVendorBilling,
  ClearVendorBillingVariables,
} from './__generated__/ClearVendorBilling';

const CLEAR_VENDOR_BILLING = gql`
  mutation ClearVendorBilling($vendorId: String!) {
    clearVendorBilling(vendorId: $vendorId) {
      id
    }
  }
`;

export function useClearBilling() {
  const showMessage = useShowMessage();

  const [clearVendorBillingMutation] = useMutation<
    ClearVendorBilling,
    ClearVendorBillingVariables
  >(CLEAR_VENDOR_BILLING);

  const clearBillingForVendor = async (vendorId: string) => {
    try {
      const result = await clearVendorBillingMutation({
        variables: { vendorId },
      });
      const updatedVendor = result.data?.clearVendorBilling;
      if (updatedVendor == null) {
        // eslint-disable-next-line no-console
        console.error(`No vendor found with ID ${vendorId}`);
        return false;
      }
      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(
        `Error clearing billing for vendor with ID ${vendorId}:`,
        error,
      );
      return false;
    }
  };

  return async (selectedVendors: Set<string>) => {
    const shouldProceed = await confirm(
      t('Clear billing?'),
      t(`This will permanently clear billing for {count} vendor(s).`, {
        count: selectedVendors.size,
      }),
    );
    if (!shouldProceed) {
      return;
    }
    const promises = [];
    for (const vendorId of selectedVendors) {
      promises.push(clearBillingForVendor(vendorId));
    }
    const results = await Promise.all(promises);
    const total = results.length;
    const successCount = results.filter((success) => success === true).length;
    const errorCount = results.filter((success) => success === false).length;
    if (errorCount) {
      showMessage(
        'error',
        t(
          '{errorCount} errors out of {total}. Check developer console for details.',
          { errorCount, total },
        ),
      );
    } else {
      showMessage(
        'success',
        t('Successfully cleared billing for {successCount} vendor(s).', {
          successCount,
        }),
      );
    }
  };
}
